import { useAtomValue, useSetAtom } from 'jotai';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionBar } from '@/lib/components/ActionBar';
import { Button, Flex, Text } from '@/lib/v2/components';
import { CopyIcon, MoveIcon, PencilIcon, RemoveIcon } from '@/lib/v2/icons/solid';

import {
  atomSelectedActionsRowsHasMaster,
  atomTotalSelectedRows,
} from '@/modules/CampaignsModule/atoms/campaignAtom';
import {
  atomToggleDeleteActionsModal,
  atomToggleDuplicateActionsModal,
  atomToggleEditActionsModal,
  atomToggleMoveActionsModal,
} from '@/modules/CampaignsModule/atoms/modalsCampaignAtom';
import {
  DeleteActions,
  DuplicateActions,
  EditActions,
  MoveActions,
} from '@/modules/CampaignsModule/components/CampaignsModal';
import { IActionIcon } from '@/modules/CampaignsModule/interfaces/Campaigns';

const ActionBarButtons = () => {
  const { t } = useTranslation();
  const totalSelectedRows = useAtomValue(atomTotalSelectedRows);

  const setToggleDuplicateActionsModal = useSetAtom(atomToggleDuplicateActionsModal);
  const setToggleMoveActionsModal = useSetAtom(atomToggleMoveActionsModal);
  const setToggleDeleteActionsModal = useSetAtom(atomToggleDeleteActionsModal);

  const handleOnCloseDuplicateModal = () => {
    setToggleDuplicateActionsModal(false);
  };

  const handleDuplicateAction = useCallback(() => {
    setToggleDuplicateActionsModal(true);
  }, [setToggleDuplicateActionsModal]);
  const setToggleEditActionsModal = useSetAtom(atomToggleEditActionsModal);
  const toggleMoveActionsModal = useAtomValue(atomToggleMoveActionsModal);
  const selectedActionsRowsHasMaster = useAtomValue(atomSelectedActionsRowsHasMaster);

  const ICONS_LIST_ACTIONS: IActionIcon[] = [
    {
      icon: <PencilIcon />,
      onClick: () => {
        setToggleEditActionsModal(true);
      },
      tooltip: t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.tooltipEdit'),
      hide: totalSelectedRows > 1,
      id: 'edit-campaign-action',
    },
    {
      icon: <MoveIcon />,
      onClick: () => {
        setToggleMoveActionsModal(true);
      },
      tooltip: t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.tooltipMove'),
      hide: selectedActionsRowsHasMaster ?? false,
      id: 'move-campaign-action',
    },
    {
      icon: <CopyIcon />,
      onClick: handleDuplicateAction,
      tooltip: t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.tooltipDuplicate'),
      hide: totalSelectedRows > 1,
      id: 'duplicate-campaign-action',
    },
    {
      icon: <RemoveIcon />,
      onClick: () => {
        setToggleDeleteActionsModal(true);
      },
      tooltip: t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.tooltipDelete'),
      hide: false,
      id: 'delete-campaign-action',
    },
  ];

  return (
    <Flex alignment="start" className=" wrapperButtons z-10 mb-[-72px] !h-14">
      <ActionBar>
        <Flex alignment="start" className="px-4">
          <div className="min-w-[120px]">
            <Text color="white" variant="text-sm">
              {totalSelectedRows > 1
                ? `${totalSelectedRows} ${t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.selected')}`
                : `${totalSelectedRows} ${t('CAMPAIGN_ACTIONS_MAIN.ACTIONS.select')}`}
            </Text>
          </div>
          <Flex withGap alignment="start" gapSize="small">
            {ICONS_LIST_ACTIONS.map((element) => {
              const { icon, hide, tooltip, onClick: handleOnClick, id } = element;
              return (
                <>
                  {!hide && (
                    <div key={id}>
                      <Button
                        fullWidth
                        outline
                        standard
                        iconLeft={icon}
                        id={id}
                        tooltip={tooltip}
                        onClick={handleOnClick}
                      />
                    </div>
                  )}
                </>
              );
            })}
          </Flex>
        </Flex>
      </ActionBar>
      <EditActions />
      {toggleMoveActionsModal && <MoveActions />}
      <DeleteActions />
      <DuplicateActions onClose={handleOnCloseDuplicateModal} />
    </Flex>
  );
};

export default memo(ActionBarButtons);
